import { resolveCrawlPolicy } from "@repo/adapter-core/crawl-policy";
import { ROLLING_TIME_RANGE } from "@repo/adapter-core/date-window";
import { GLOBAL_TIME_RANGE_OPTIONS } from "@repo/adapter-core/types";
import { Button } from "@repo/ui/button";
import { Checkbox } from "@repo/ui/checkbox";
import { Input } from "@repo/ui/input";
import { Label } from "@repo/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@repo/ui/select";
import { Switch } from "@repo/ui/switch";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import {
  CADENCE_PRESETS,
  type Cadence,
  cadenceDateConfig,
  cadenceForCron,
  describeCadence,
} from "@/components/jobs/schedulePresets";
import { useToast } from "@/hooks/use-toast";
import { type CrawlJobConfig, fetchSuburbLists, type Job, updateJobSchedule } from "@/lib/api";

type ScheduleChoice = "manual" | Cadence;
const FIXED_DATES = "absolute";

interface JobScheduleCardProps {
  job: Job;
  onSaved?: () => void;
}

/**
 * Edits a job after creation: when it runs, which period each run covers, the
 * crawl policy, and its suburb lists. Mount with a key that changes when the job
 * row changes so the form re-reads the saved values.
 */
export const JobScheduleCard = ({ job, onSaved }: JobScheduleCardProps) => {
  const { toast } = useToast();
  const policy = resolveCrawlPolicy(job.config);
  const savedListIds = (job.job_suburb_lists ?? []).map((row) => row.suburb_list_id);

  const [choice, setChoice] = useState<ScheduleChoice>(
    job.run_type === "scheduled" && job.cron_expression
      ? cadenceForCron(job.cron_expression)
      : "manual",
  );
  const [customCron, setCustomCron] = useState(job.cron_expression ?? CADENCE_PRESETS.weekly.cron);
  const [timeRange, setTimeRange] = useState(
    job.config.dateFilterMode === "absolute" ? FIXED_DATES : (job.config.timeRange ?? "all"),
  );
  const [skipComplete, setSkipComplete] = useState(policy.skipComplete);
  const [completeMissing, setCompleteMissing] = useState(policy.completeMissing);
  const [suburbListIds, setSuburbListIds] = useState<string[]>(savedListIds);
  const [saving, setSaving] = useState(false);

  const { data: suburbLists = [], isLoading: listsLoading } = useQuery({
    queryKey: ["suburb-lists"],
    queryFn: fetchSuburbLists,
  });

  const preset = choice === "weekly" || choice === "monthly" ? choice : null;
  const hasSuburbs = suburbListIds.length > 0 || (job.job_suburbs?.length ?? 0) > 0;
  const cronValid = choice !== "custom" || customCron.trim().split(/\s+/).length === 5;

  const buildConfig = (): CrawlJobConfig => {
    const dateConfig: Partial<CrawlJobConfig> = preset
      ? cadenceDateConfig(preset)
      : timeRange === FIXED_DATES
        ? {}
        : {
            dateFilterMode: "relative",
            timeRange: timeRange === "all" ? undefined : timeRange,
            rollingFirstRunDays:
              timeRange === ROLLING_TIME_RANGE ? job.config.rollingFirstRunDays : undefined,
            dateFrom: undefined,
            dateTo: undefined,
          };
    return {
      ...job.config,
      ...dateConfig,
      crawlPolicy: { ...job.config.crawlPolicy, skipComplete, completeMissing },
    };
  };

  const save = async () => {
    setSaving(true);
    try {
      await updateJobSchedule(job, {
        runType: choice === "manual" ? "immediate" : "scheduled",
        cronExpression:
          choice === "manual" ? null : preset ? CADENCE_PRESETS[preset].cron : customCron,
        config: buildConfig(),
        suburbListIds,
      });
      toast({ title: "Job updated", description: "Schedule, window and policy saved." });
      onSaved?.();
    } catch (error) {
      toast({
        title: "Could not save",
        description: error instanceof Error ? error.message : String(error),
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 rounded-lg border p-4">
      <div>
        <p className="text-sm font-medium">Edit schedule & policy</p>
        <p className="text-xs text-muted-foreground">
          Changes apply from the next run; a run in progress keeps the settings it started with.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor={`schedule-${job.id}`}>Runs</Label>
          <Select value={choice} onValueChange={(value) => setChoice(value as ScheduleChoice)}>
            <SelectTrigger id={`schedule-${job.id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="manual">Manually only</SelectItem>
              <SelectItem value="weekly">{CADENCE_PRESETS.weekly.label}</SelectItem>
              <SelectItem value="monthly">{CADENCE_PRESETS.monthly.label}</SelectItem>
              <SelectItem value="custom">Custom cron (UTC)</SelectItem>
            </SelectContent>
          </Select>
          {choice === "custom" && (
            <Input
              aria-label="Cron expression"
              className="font-mono"
              value={customCron}
              onChange={(e) => setCustomCron(e.target.value)}
              placeholder="0 16 * * 0"
            />
          )}
          {preset && <p className="text-xs text-muted-foreground">{describeCadence(preset)}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor={`window-${job.id}`}>Each run covers</Label>
          <Select
            value={preset ? ROLLING_TIME_RANGE : timeRange}
            onValueChange={setTimeRange}
            disabled={Boolean(preset)}
          >
            <SelectTrigger id={`window-${job.id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {GLOBAL_TIME_RANGE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
              {job.config.dateFilterMode === "absolute" && (
                <SelectItem value={FIXED_DATES}>
                  Fixed dates ({job.config.dateFrom ?? "…"} to {job.config.dateTo ?? "…"})
                </SelectItem>
              )}
            </SelectContent>
          </Select>
          {preset && (
            <p className="text-xs text-muted-foreground">
              Weekly and monthly runs always cover the period since the last successful run.
            </p>
          )}
        </div>
      </div>

      <div className="space-y-3 border-t pt-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <Label htmlFor={`skip-complete-${job.id}`} className="text-sm">
              Skip sales we already have
            </Label>
            <p className="text-xs text-muted-foreground">
              Pages whose sale we hold completely are not fetched again.
            </p>
          </div>
          <Switch
            id={`skip-complete-${job.id}`}
            checked={skipComplete}
            onCheckedChange={setSkipComplete}
          />
        </div>
        <div className="flex items-start justify-between gap-4">
          <div>
            <Label htmlFor={`complete-missing-${job.id}`} className="text-sm">
              Complete missing data
            </Label>
            <p className="text-xs text-muted-foreground">
              Fetch held sales that are incomplete, at most once per site every 30 days.
            </p>
          </div>
          <Switch
            id={`complete-missing-${job.id}`}
            checked={skipComplete && completeMissing}
            disabled={!skipComplete}
            onCheckedChange={setCompleteMissing}
          />
        </div>
      </div>

      <div className="space-y-2 border-t pt-4">
        <Label>Suburb lists</Label>
        {listsLoading ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : suburbLists.length === 0 ? (
          <p className="text-xs text-muted-foreground">No suburb lists yet.</p>
        ) : (
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            {suburbLists.map((list) => (
              <div key={list.id} className="flex items-center gap-2 text-sm">
                <Checkbox
                  id={`list-${job.id}-${list.id}`}
                  checked={suburbListIds.includes(list.id)}
                  onCheckedChange={(checked) =>
                    setSuburbListIds((prev) =>
                      checked ? [...prev, list.id] : prev.filter((id) => id !== list.id),
                    )
                  }
                />
                <Label htmlFor={`list-${job.id}-${list.id}`} className="cursor-pointer font-normal">
                  {list.name}
                  {list.item_count !== undefined && (
                    <span className="ml-1 text-xs text-muted-foreground">({list.item_count})</span>
                  )}
                </Label>
              </div>
            ))}
          </div>
        )}
        {(job.job_suburbs?.length ?? 0) > 0 && (
          <p className="text-xs text-muted-foreground">
            Plus {job.job_suburbs?.length} suburbs picked individually when the job was created.
          </p>
        )}
        {!hasSuburbs && <p className="text-xs text-destructive">Pick at least one suburb list.</p>}
      </div>

      <div className="flex justify-end">
        <Button type="button" onClick={save} disabled={saving || !hasSuburbs || !cronValid}>
          {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Save changes
        </Button>
      </div>
    </div>
  );
};
